// Updated imports for Firebase v9
import { initializeApp, getApps } from 'firebase/app';
import { 
  getAuth, 
  signInWithPhoneNumber, 
  PhoneAuthProvider, 
  signInWithCredential, 
  onAuthStateChanged, 
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import api from './api';

// Initialize Firebase (replace with your config)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase only if it hasn't been initialized already
if (!getApps().length) {
  initializeApp(firebaseConfig);
}

// Get Firebase auth instance
const auth = getAuth();

// Get current auth token
export const getAuthToken = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return null;
  }
  
  try {
    const token = await currentUser.getIdToken(true);
    return token;
  } catch (error) {
    console.error('Failed to get auth token', error);
    return null;
  }
};

// Register new user with phone number
export const registerUser = async (phoneNumber) => {
  try {
    // Start phone auth verification
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
    return confirmationResult;
  } catch (error) {
    console.error('Failed to register user', error);
    throw error;
  }
};

// Verify SMS code and login user
export const loginUser = async (verificationId, code) => {
  try {
    // Create credential with verification ID and code
    const credential = PhoneAuthProvider.credential(verificationId, code);
    
    // Sign in with credential
    const userCredential = await signInWithCredential(auth, credential);
    
    // Return user data
    return userCredential.user;
  } catch (error) {
    console.error('Failed to verify code', error);
    throw error;
  }
};

// Get current logged in user
export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, user => {
      unsubscribe();
      resolve(user);
    }, error => {
      reject(error);
    });
  });
};

// Logout user
export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Failed to logout user', error);
    throw error;
  }
};

// ADMIN AUTHENTICATION METHODS
// --------------------------

// Login admin with email and password
export const loginAdmin = async (email, password) => {
  try {
    // Use Firebase email/password authentication
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Check if the user has admin record in Firestore
    // This is done by making a request to a protected admin endpoint
    const response = await api.get('/api/admins/me');
    
    // Get the admin data
    const adminData = response.data;
    
    // Store admin UID
    localStorage.setItem('admin-uid', userCredential.user.uid);
    localStorage.setItem('admin-role', adminData.role);
    
    return adminData;
  } catch (error) {
    console.error('Failed to login admin', error);
    
    // If Firebase authentication succeeds but the user is not an admin,
    // we need to sign out and throw an error
    if (error.response && error.response.status === 403) {
      await signOut(auth);
      throw new Error('You do not have admin privileges');
    }
    
    throw error;
  }
};

// Get current admin
export const getCurrentAdmin = async () => {
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    return null;
  }
  
  try {
    // Make sure we have the latest token
    await currentUser.getIdToken(true);
    
    // Get the admin data using the current user
    const response = await api.get('/api/admins/me');
    return {
      id: currentUser.uid,
      email: currentUser.email,
      ...response.data
    };
  } catch (error) {
    console.error('Failed to get current admin', error);
    
    // Clear stored admin data on error
    localStorage.removeItem('admin-uid');
    localStorage.removeItem('admin-role');
    
    // If it's a 404 or 403, the user is not an admin
    if (error.response && (error.response.status === 404 || error.response.status === 403)) {
      // Sign out the user from Firebase if they're not an admin
      await signOut(auth);
    }
    
    return null;
  }
};
// Admin logout
export const logoutAdmin = async () => {
  // Clear stored admin data
  localStorage.removeItem('admin-uid');
  localStorage.removeItem('admin-role');
  
  // Use the existing Firebase signOut method
  await signOut(auth);
};

// Get admin token for API requests (same as regular auth token)
export const getAdminToken = async () => {
  return getAuthToken();
};

// Get admin UID for API requests
export const getAdminUid = () => {
  return localStorage.getItem('admin-uid');
};

export default auth;